/**
 * Czech and Slovak typography at render time - ONE rule shared by every i18n
 * layer (mobile i18next resources and API errors, web dictionaries, web
 * `t()` strings, API errors and guide topics, admin-web dictionary, web-pro
 * strings, status-web strings, the BOZP player chrome, the API's e-mail
 * translations and push/feed templates). Pure, dependency-free.
 *
 * The rule: a one-letter preposition never ends a line. In cs and sk copy the
 * ordinary space (U+0020) after a STANDALONE one-letter word from the set
 * `v k s z o u` (either case) becomes a no-break space (U+00A0), so the word
 * wraps together with the next one. The letter set is the owner's call
 * (2026-09-26): the one-letter prepositions only; the conjunctions `a`/`i`
 * stay breakable.
 *
 *  - Standalone = at the start of the string, or right after whitespace
 *    (U+00A0 and newline included) or an opening character: `„ ‚ ( [ "`.
 *  - Exactly one ordinary space must follow, then a non-space character. A
 *    letter followed by punctuation (`v.`), a newline, two spaces or the end
 *    of the string is left alone.
 *  - Chains bind in one pass (`s v domě` -> `s·v·domě`), because the
 *    standalone check reads the ORIGINAL text, not the replaced one.
 *  - URLs and e-mail addresses carry no spaces and never put a one-letter
 *    word after a separator, so they never match.
 *  - Idempotent: an already bound space is U+00A0, which never matches again.
 *
 * Applied to catalog VALUES at load (`typesetCatalog`), never to interpolated
 * output, so user data inside `{x}` / `{{x}}` is never altered. Catalogs stay
 * free of U+00A0: never type one into a catalog, the loaders add it.
 *
 * No lookbehind on purpose: a regex literal with `(?<=` is a SyntaxError that
 * kills the whole bundle on Safari < 16.4, and this module ships to the web
 * client.
 */

/** U+00A0 NO-BREAK SPACE. */
export const NBSP = '\u00a0';

/** Locales whose copy gets the one-letter binding (language subtag). */
export const TYPOGRAPHY_LANGUAGES = ['cs', 'sk'] as const;
export type TypographyLanguage = (typeof TYPOGRAPHY_LANGUAGES)[number];

/** A one-letter candidate, one ordinary space, then a non-space character. */
const ONE_LETTER_THEN_SPACE = /([vkszouVKSZOU]) (?=\S)/g;

/** Characters after which a word starts: `„` `‚` `(` `[` `"`. */
const OPENING_CHARACTERS = new Set(['„', '‚', '(', '[', '"']);

const WHITESPACE = /\s/;

function startsWord(previous: string | undefined): boolean {
  return previous === undefined || WHITESPACE.test(previous) || OPENING_CHARACTERS.has(previous);
}

/**
 * Bind every standalone one-letter preposition to the following word with a
 * U+00A0. Only call it on Czech or Slovak text; `typographyFor` picks it by
 * locale.
 */
export function applyCzechTypography(text: string): string {
  return text.replace(
    ONE_LETTER_THEN_SPACE,
    (match: string, letter: string, offset: number, source: string) =>
      startsWord(source[offset - 1]) ? `${letter}${NBSP}` : match,
  );
}

function identity(text: string): string {
  return text;
}

/** The typography language of a locale tag (`cs`, `cs-CZ`, `sk_SK`), or `null`. */
export function typographyLanguage(locale: string | null | undefined): TypographyLanguage | null {
  const language = locale?.toLowerCase().split(/[-_]/)[0];
  return (TYPOGRAPHY_LANGUAGES as readonly string[]).includes(language ?? '')
    ? (language as TypographyLanguage)
    : null;
}

/**
 * The text pass for a locale: `applyCzechTypography` for cs/sk, the identity
 * for every other locale (en, uk, ...), so non-Czech text stays byte-identical.
 */
export function typographyFor(locale: string | null | undefined): (text: string) => string {
  return typographyLanguage(locale) ? applyCzechTypography : identity;
}

function typesetValue(value: unknown, pass: (text: string) => string): unknown {
  if (typeof value === 'string') return pass(value);
  if (Array.isArray(value)) return value.map((item) => typesetValue(item, pass));
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, typesetValue(item, pass)]),
    );
  }
  return value;
}

/** Catalog object -> its typeset copy per language, so each catalog is typeset once. */
const typesetCache = new WeakMap<object, Map<TypographyLanguage, unknown>>();

/**
 * A translation catalog with every string VALUE typeset for `locale` (keys,
 * numbers and structure untouched). For a non-cs/sk locale it returns the
 * catalog itself. Memoized per catalog object and language, so a loader may
 * call it on every load: an imported JSON module is the same object each
 * time, and the walk runs once.
 */
export function typesetCatalog<T>(catalog: T, locale: string | null | undefined): T {
  const language = typographyLanguage(locale);
  if (!language || catalog === null || typeof catalog !== 'object') return catalog;
  let perLanguage = typesetCache.get(catalog);
  if (!perLanguage) {
    perLanguage = new Map();
    typesetCache.set(catalog, perLanguage);
  }
  if (!perLanguage.has(language)) {
    perLanguage.set(language, typesetValue(catalog, applyCzechTypography));
  }
  // Sound by construction: typesetValue maps string -> string and rebuilds
  // arrays/objects key for key, so the result has exactly T's shape.
  return perLanguage.get(language) as T;
}
